import { useMutation, useQuery } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "@/components/confirm-dialog";
import { NotificationSettings } from "@/components/notification-settings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { authClient } from "@/lib/auth-client";
import { getErrorMessage } from "@/lib/form-errors";
import { useUser } from "@/lib/user-context";
import { queryClient, trpc } from "@/utils/trpc";

type SessionUser = {
  id?: string;
  email: string;
  [key: string]: unknown;
};

type Session = {
  user: SessionUser;
  [key: string]: unknown;
} | null;

type UserProfile =
  | {
      createdAt?: string | null;
      updatedAt?: string | null;
      [key: string]: unknown;
    }
  | null
  | undefined;

type AccountField = {
  label: string;
  getValue: (session: Session, userProfile: UserProfile) => string;
  render: (value: string) => ReactNode;
};

const accountFields: readonly AccountField[] = [
  {
    label: "Email",
    getValue: (session) => session?.user?.email ?? "N/A",
    render: (value) => <span className="font-medium">{value}</span>,
  },
  {
    label: "ID",
    getValue: (session) =>
      typeof session?.user?.id === "string" ? session.user.id : "N/A",
    render: (value) => <CopyableId value={value} />,
  },
  {
    label: "Account Status",
    getValue: () => "Active",
    render: () => <Badge>Active</Badge>,
  },
  {
    label: "Account Created",
    getValue: (_session, userProfile) =>
      userProfile?.createdAt
        ? new Date(userProfile.createdAt).toLocaleDateString()
        : "N/A",
    render: (value) => <span className="font-medium">{value}</span>,
  },
  {
    label: "Last Updated",
    getValue: (_session, userProfile) =>
      userProfile?.updatedAt
        ? new Date(userProfile.updatedAt).toLocaleDateString()
        : "N/A",
    render: (value) => <span className="font-medium">{value}</span>,
  },
] as const;

function CopyableId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeout = window.setTimeout(() => {
      setCopied(false);
    }, 2000);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  if (!value || value === "N/A") {
    return <span className="font-medium">N/A</span>;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch (error) {
      console.error("Failed to copy user ID", error);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? "Copied to clipboard" : "Copy user ID"}
      aria-label={copied ? "User ID copied to clipboard" : "Copy user ID"}
      className="font-meta inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-sm text-foreground transition hover:bg-muted focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/45 focus-visible:outline-none"
    >
      <span className="max-w-[10rem] truncate">{value}</span>
      <Copy className="h-4 w-4" aria-hidden="true" />
      <span
        className="min-w-[3.25rem] text-right text-xs text-muted-foreground"
        aria-live="polite"
      >
        {copied ? "Copied" : "Copy"}
      </span>
    </button>
  );
}

export default function AccountInformation() {
  return <AccountInformationSection />;
}

export function AccountInformationSection() {
  const { session } = useUser();
  const { data: userProfile } = useQuery(trpc.getUserProfile.queryOptions());
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteAccountMutation = useMutation({
    ...trpc.deleteAccount.mutationOptions(),
    onSuccess: async () => {
      queryClient.clear();
      try {
        await authClient.signOut();
      } catch (error) {
        console.error("Sign out error after account deletion:", error);
      } finally {
        navigate("/");
      }
    },
    onError: (error) => {
      setDeleteError(getErrorMessage(error, "Failed to delete account"));
    },
  });

  if (!session) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Account Information</CardTitle>
          <CardDescription>Your account details and settings</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="divide-y text-sm">
            {accountFields.map(({ label, getValue, render }) => {
              const value = getValue(session, userProfile);
              return (
                <li
                  key={label}
                  className="flex justify-between items-center py-4"
                >
                  <span className="text-muted-foreground">{label}</span>
                  {render(value)}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <div>
        <NotificationSettings />
      </div>

      <Card className="border-destructive/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>
            Permanently delete your account and all associated data. This action
            cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="destructive"
            onClick={() => {
              setDeleteError(null);
              setShowDeleteDialog(true);
            }}
          >
            Delete Account
          </Button>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete your account?"
        description={
          <span>
            This will permanently erase all your personal data, sign you out,
            and cancel any active subscription at the end of the billing period.{" "}
            <strong>This cannot be undone.</strong>
          </span>
        }
        confirmLabel="Delete my account"
        pendingLabel="Deleting…"
        pending={deleteAccountMutation.isPending}
        error={deleteError}
        onConfirm={() => deleteAccountMutation.mutate()}
      />
    </div>
  );
}
